import { API_BASE_URL, ApiError, adminApi } from './api';
import { cacheService } from './cacheService';
import { 
  CompanionAdapter, 
  ApiCompanion, 
  ApiCompanionListResponse, 
  NormalizedCompanion 
} from '../adapters/companionAdapter';
import { ChatbotPersonality } from '../types/chatbot';

export interface Tag {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

export interface CompanionAsset {
  photo_id: string;
  companion_id: string;
  presigned_url: string;
  is_primary: boolean;
  asset_type: "image" | "video";
  created_at: string;
}

// Re-export types from adapter for backward compatibility
export type Companion = ApiCompanion;
export type CompanionListResponse = ApiCompanionListResponse;

export interface CompanionCreate {
  name: string;
  description?: string;
  personality?: any;
  age?: number;
  is_public: boolean;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = localStorage.getItem('access_token');
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(0, 'Network error occurred');
  }
}

class CompanionService {
  /**
   * Get all companions with automatic data transformation
   */
  async getAllCompanions(useCache: boolean = true): Promise<CompanionListResponse> {
    // Check cache first
    if (useCache) {
      const cached = cacheService.getCompanions();
      if (cached) {
        console.log('🚀 Using cached companions data');
        return cached;
      }
    }

    // Try multiple endpoints for reliability
    const endpoints = [
      '/api/companions',
      '/api/companions/',
      '/api/companions/public',
      '/api/companions/public?include_category=true'
    ];

    let data: CompanionListResponse | null = null;
    let lastError: Error | null = null;

    for (const endpoint of endpoints) {
      try {
        data = await apiRequest<CompanionListResponse>(endpoint);
        console.log(`✅ Successfully fetched companions from ${endpoint}`);
        break;
      } catch (error) {
        console.warn(`❌ Failed to fetch from ${endpoint}:`, error);
        lastError = error as Error;
        continue;
      }
    }

    if (!data) {
      throw lastError || new Error('All companion endpoints failed');
    }

    // Cache the result
    if (useCache) {
      cacheService.setCompanions(data);
    }

    return data;
  }

  /**
   * Get all companions as normalized data
   */
  async getAllCompanionsNormalized(useCache: boolean = true): Promise<NormalizedCompanion[]> {
    const response = await this.getAllCompanions(useCache);
    return CompanionAdapter.apiResponseToNormalized(response);
  }

  /**
   * Get all companions as chatbot personalities
   */
  async getAllCompanionsChatbots(useCache: boolean = true): Promise<ChatbotPersonality[]> {
    const response = await this.getAllCompanions(useCache);
    return CompanionAdapter.apiResponseToChatbots(response);
  }

  /**
   * Get companion by ID with automatic transformation
   */
  async getCompanionById(companionId: string): Promise<Companion> {
    return apiRequest<Companion>(`/api/companions/${companionId}`);
  }

  /**
   * Get companion by ID as normalized data
   */
  async getCompanionByIdNormalized(companionId: string): Promise<NormalizedCompanion> {
    const apiCompanion = await this.getCompanionById(companionId);
    return CompanionAdapter.apiToNormalized(apiCompanion);
  }

  /**
   * Get companion by ID as chatbot personality
   */
  async getCompanionByIdChatbot(companionId: string): Promise<ChatbotPersonality> {
    const apiCompanion = await this.getCompanionById(companionId);
    return CompanionAdapter.apiToChatbot(apiCompanion);
  }

  /**
   * Get companions with categories (legacy method)
   */
  async getCompanionsWithCategories(): Promise<CompanionListResponse> {
    return this.getAllCompanions();
  }

  async createCompanion(companion: CompanionCreate): Promise<Companion> {
    return apiRequest<Companion>('/api/companions', {
      method: 'POST',
      body: JSON.stringify(companion),
    });
  }

  async likeCompanion(companionId: string): Promise<void> {
    return apiRequest<void>(`/api/companions/${companionId}/like`, {
      method: 'POST',
    });
  }

  async incrementMessages(companionId: string): Promise<void> {
    return apiRequest<void>(`/api/companions/${companionId}/message`, {
      method: 'POST',
    });
  }

  async addCompanionAsset(companionId: string, photoId: string, isPrimary: boolean = false, assetType: "image" | "video" = "image"): Promise<CompanionAsset> {
    const params = new URLSearchParams({
      photo_id: photoId,
      is_primary: isPrimary.toString(),
      asset_type: assetType
    });
    
    return apiRequest<CompanionAsset>(`/api/companions/${companionId}/assets?${params}`, {
      method: 'POST',
    });
  }

  async deleteCompanionAsset(companionId: string, assetId: string): Promise<void> {
    return adminApi.deleteCompanionAsset(companionId, assetId);
  }

  // Admin methods - using the correct admin endpoints
  async updateCompanion(companionId: string, updates: Partial<Companion>): Promise<Companion> {
    return adminApi.updateCompanion<Companion>(companionId, updates);
  }

  async deleteCompanion(companionId: string): Promise<void> {
    return apiRequest<void>(`/api/companions/${companionId}`, {
      method: 'DELETE',
    });
  }

  async toggleCompanionStatus(companionId: string, isActive: boolean): Promise<Companion> {
    return apiRequest<Companion>(`/api/companions/${companionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    });
  }
}

export const companionService = new CompanionService(); 